import { createContext, ReactNode, useContext, useMemo } from 'react';
import { UseQueryState } from 'urql';
import useOfflineQuery from 'hooks/useOfflineQuery';
import { ChartDashboardNewQuery, useChartDashboardNewQuery } from 'generated/graphql';
import { useIsReportsPdfMode } from '../useIsReportsPdfMode';
import { useReportsDashboard } from '../useReportsDashboard';

interface ChartDashboardContextProps {
  chartDashboardNewResult: UseQueryState<ChartDashboardNewQuery>;
  chartDashboardFetching: boolean;
  refreshChartDashboardNewResult: () => void;
}

const Context = createContext<ChartDashboardContextProps | undefined>(undefined);

export const ChartDashboardProvider = ({ children }: { children: ReactNode }) => {
  const { reportsDashboardNewQueryInput } = useReportsDashboard();
  const isReportsPdfMode = useIsReportsPdfMode();

  const [chartDashboardNewResult, refreshChartDashboardNewResult] = useOfflineQuery(
    'chart-dashboard-new-query',
    useChartDashboardNewQuery,
    {
      variables: { input: reportsDashboardNewQueryInput },
      requestPolicy: 'network-only',
      pause: isReportsPdfMode,
    },
  );

  const chartDashboardFetching = (chartDashboardNewResult.fetching || chartDashboardNewResult.stale) ?? false;

  const value = useMemo<ChartDashboardContextProps>(
    () => ({
      chartDashboardNewResult,
      chartDashboardFetching,
      refreshChartDashboardNewResult,
    }),
    [chartDashboardNewResult, chartDashboardFetching, refreshChartDashboardNewResult],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useChartDashboard = () => {
  const result = useContext(Context);
  if (!result) {
    throw new Error('useChartDashboard must be used within a ChartDashboardProvider');
  }
  return result;
};
